import { motion, useReducedMotion } from "framer-motion";

const RISE = 12; // px slide-up distance for the entrance animation

/**
 * Fades + rises children in — used to give scrolling feeds/carousels a less
 * static feel on mobile, where hover animations don't apply.
 *
 * By default each instance watches its own entry into the viewport via
 * `whileInView`. Pass `inView` to instead have a parent drive the trigger:
 * for a horizontal carousel, checking each card's own visibility would replay
 * the animation every time a card is scrolled sideways into view, since that
 * counts as "entering the viewport" too. Driving every card off one check of
 * the row's vertical position (unaffected by its own horizontal scroll)
 * avoids that — see Carousel.js.
 *
 * `margin` insets the viewport for that check (as CSS margin, e.g.
 * "0px 0px -20% 0px" to wait until the element is a fifth of the way up the
 * screen); `duration` slows the entrance for content meant to be watched.
 *
 * Renders a plain div (no motion) under prefers-reduced-motion.
 */
export default function MotionInView({
  delay = 0,
  amount = 0.2,
  margin,
  duration = 0.3,
  inView,
  children,
  ...rest
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div {...rest}>{children}</div>;

  const isControlled = inView !== undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: RISE }}
      transition={{ duration, delay, ease: "easeOut" }}
      {...(isControlled
        ? { animate: inView ? { opacity: 1, y: 0 } : { opacity: 0, y: RISE } }
        : { whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount, margin } })}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
